import { useState } from 'react'
import { X, Sparkles, HandCoins, ChevronRight, Layers, Gauge, ShieldAlert } from 'lucide-react'
import { formatOdds, formatProb, resolvePickBadgeStatus, formatKickoff } from '@/lib/utils'
import { StatusBadge } from './StatusBadge'
import { parlayDisplayStatus } from '@/lib/badges'
import { TeamCrest } from './TeamCrest'
import { RunBadge } from './RunBadge'
import { RegisterBetModal } from './RegisterBetModal'
import { PlayedBadge } from './PlayedBadge'
import type { Parlay } from '@/types/miniApp'

const CONFIDENCE_LABEL: Record<string, string> = {
  ALTA: 'Alta', MEDIA: 'Media', BAJA: 'Baja',
}

function qualityLabel(score: number | null | undefined): string {
  if (score == null) return 'N/D'
  if (score >= 75) return 'Alta'
  if (score >= 50) return 'Media'
  return 'Baja'
}

// Sprint "SGP Armenia: Gate + T-20 + Criterio Real" (03/09/2026, Sección "Presentación" del
// encargo) -- el cliente debe saber de inmediato QUÉ competición está analizando el SGP, sin
// abrir el detalle de cada pierna. `firstLeg.league`/`.kickoff_utc` ya vienen del mismo
// `_build_leg_detail` que arma cada pierna (dashboard.py) -- nunca un campo nuevo, solo se
// renderizaba antes. Mismo formato que `PickDetailSheet.tsx::kickoffLabel` para consistencia
// visual entre superficies.
// P1.2 (01/10/2026, Timezone Global -- Requisito B): delega al formatter central
// (`resolveDisplayTimeZone()`) en vez de `toLocaleString()` sin zona explícita.
function kickoffLabel(iso?: string | null): string {
  if (!iso) return 'Prematch'
  return formatKickoff(iso)
}

/** Sección 9 del encargo P0.23 -- Same Game Parlay debe sentirse ESPECIAL, nunca mezclado
 * visualmente con Conservador/Balanceado/Agresivo (`ParlayCard.tsx`). "Un partido. Múltiples
 * mercados." Solo se genera con datos suficientes -- si no existe hoy, `ParlaysPage.tsx`
 * muestra el estado vacío explícito en vez de fabricar uno. */
export function SameGameParlayCard({ parlay }: { parlay: Parlay }) {
  const [showDrawer, setShowDrawer] = useState(false)
  const [showPlayModal, setShowPlayModal] = useState(false)
  const sortedLegs = parlay.legs.slice().sort((a, b) => a.leg_order - b.leg_order)
  const firstLeg = sortedLegs[0]
  const homeName = (firstLeg?.home_team as string) ?? 'Local'
  const awayName = (firstLeg?.away_team as string) ?? 'Visitante'
  const league = (firstLeg?.league as string | null | undefined) ?? null
  const kickoffUtc = (firstLeg?.kickoff_utc as string | null | undefined) ?? null

  // `parlay.explanation` -- tesis real persistida por el SGP Intelligence Engine
  // (`build_sgp_explanation`, ver docstring de esa función): la ÚLTIMA línea siempre
  // describe qué podría romper el SGP (construcción determinista del backend), se separa
  // aquí por prefijo real -- nunca una segunda fuente inventada en el frontend.
  const explanationLines: string[] = Array.isArray(parlay.explanation)
    ? parlay.explanation
    : typeof parlay.explanation === 'string' && parlay.explanation
      ? [parlay.explanation]
      : []
  const breaksItPrefix = 'Qué podría romper este SGP:'
  const breaksItRaw = explanationLines.find((l) => l.startsWith(breaksItPrefix))
  const breaksItLine = breaksItRaw ? breaksItRaw.slice(breaksItPrefix.length).trim() : null
  const thesisLines = explanationLines.filter((l) => l !== breaksItRaw)

  return (
    <>
      <div
        onClick={() => setShowDrawer(true)}
        className="relative overflow-hidden cursor-pointer rounded-2xl border border-violet/40 bg-gradient-to-b from-violet/10 via-panel to-panel p-4 space-y-3.5 shadow-[0_4px_25px_rgba(139,92,246,0.12)] active-scale hover-float"
      >
        <div className="absolute -top-10 -right-10 h-28 w-28 rounded-full opacity-25 blur-2xl pointer-events-none" style={{ background: 'rgb(var(--jd-violet))' }} />

        <div className="flex items-center justify-between relative z-10">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-[10px] font-black uppercase tracking-wider bg-violet/20 text-violet border border-violet/40">
            <Sparkles className="h-3.5 w-3.5" /> Same Game Parlay
          </span>
          <div className="flex items-center gap-1.5">
            {parlay.run_type && <RunBadge runType={parlay.run_type} compact />}
            <StatusBadge status={parlayDisplayStatus(parlay.status, parlay.is_bettable)} />
          </div>
        </div>

        {(league || kickoffUtc) && (
          <p className="text-[10px] text-text-dim text-center font-bold truncate relative z-10">
            {league ?? 'Competición Oficial'}
            {kickoffUtc && <> · {kickoffLabel(kickoffUtc)}</>}
          </p>
        )}

        <div className="flex items-center justify-center gap-3">
          <div className="flex flex-col items-center gap-1.5 w-24 text-center">
            <TeamCrest src={firstLeg?.home_team_logo as string} alt={homeName} size={44} />
            <span className="text-xs font-black text-text line-clamp-1">{homeName}</span>
          </div>
          <span className="text-sm font-mono font-black text-text-dim">VS</span>
          <div className="flex flex-col items-center gap-1.5 w-24 text-center">
            <TeamCrest src={firstLeg?.away_team_logo as string} alt={awayName} size={44} />
            <span className="text-xs font-black text-text line-clamp-1">{awayName}</span>
          </div>
        </div>

        <p className="text-[10px] text-text-dim text-center font-bold uppercase tracking-wider">
          {sortedLegs.length} mercados combinados
        </p>

        <div className="space-y-1.5">
          {sortedLegs.slice(0, 4).map((leg) => (
            <div key={leg.leg_order} className="flex items-center gap-2 text-[11px] text-text-muted">
              <span className="h-1.5 w-1.5 rounded-full bg-violet shrink-0" />
              <span className="truncate">{(leg.selection_label ?? leg.selection) as string}</span>
            </div>
          ))}
          {sortedLegs.length > 4 && (
            <p className="text-[10px] text-text-dim italic pl-3.5">+ {sortedLegs.length - 4} más…</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2.5 pt-2 border-t border-border/30">
          <div className="bg-panel-elevated/50 p-2.5 rounded-xl border border-border/40 text-center">
            <p className="text-[9px] font-bold uppercase tracking-wider text-text-dim">Cuota Total</p>
            <p className="text-lg font-black text-violet font-mono">@{formatOdds(parlay.combined_odds)}</p>
          </div>
          <div className="bg-panel-elevated/50 p-2.5 rounded-xl border border-border/40 text-center">
            <p className="text-[9px] font-bold uppercase tracking-wider text-text-dim">Prob. Combinada</p>
            <p className="text-lg font-black text-text font-mono">{formatProb(parlay.joint_probability_adjusted)}</p>
          </div>
        </div>

        <div className="flex items-center justify-between pt-0.5 text-[10px] font-bold text-violet">
          <span>Ver análisis del partido</span>
          <ChevronRight className="h-3.5 w-3.5" />
        </div>

        {parlay.status !== 'no_formado' && (
          parlay.my_bet ? (
            <PlayedBadge bet={parlay.my_bet} />
          ) : (
            <button
              onClick={(e) => { e.stopPropagation(); setShowPlayModal(true) }}
              className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl bg-panel-elevated border border-violet/40 text-violet hover:bg-violet/10 text-[10px] font-black uppercase tracking-wider transition"
            >
              <HandCoins className="h-3.5 w-3.5" /> La jugué
            </button>
          )
        )}
      </div>

      {showDrawer && (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowDrawer(false)} />
          <div className="relative w-full max-w-lg bg-panel rounded-t-3xl border-t border-violet/30 max-h-[85vh] overflow-y-auto animate-fade-in-up shadow-2xl">
            <div className="sticky top-0 bg-panel border-b border-border px-5 py-3.5 flex items-center justify-between z-10">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-violet" />
                <p className="text-xs font-black uppercase tracking-wider text-text">Same Game Parlay</p>
              </div>
              <button onClick={() => setShowDrawer(false)} className="p-1.5 rounded-xl bg-panel-elevated text-text-dim hover:text-text">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              <div className="flex items-center justify-between text-2xs text-text-dim font-bold px-1">
                <span className="truncate">{league ?? 'Competición Oficial'}</span>
                <span className="font-mono shrink-0">{kickoffLabel(kickoffUtc)}</span>
              </div>
              <div className="flex items-center justify-center gap-3">
                <div className="flex flex-col items-center gap-1.5 w-24 text-center">
                  <TeamCrest src={firstLeg?.home_team_logo as string} alt={homeName} size={48} />
                  <span className="text-xs font-black text-text line-clamp-2">{homeName}</span>
                </div>
                <span className="text-lg font-mono font-black text-text-dim">VS</span>
                <div className="flex flex-col items-center gap-1.5 w-24 text-center">
                  <TeamCrest src={firstLeg?.away_team_logo as string} alt={awayName} size={48} />
                  <span className="text-xs font-black text-text line-clamp-2">{awayName}</span>
                </div>
              </div>

              {/* Sprint "Product Experience V2" (10/09/2026, mockup aprobado -- Sección
                  "SGP"): "POR QUÉ ESTE SGP" usa la tesis real del SGP Intelligence Engine
                  (`parlay.explanation`, ya persistida por `build_sgp_explanation` a partir
                  de la Match Story real del partido) -- nunca el texto genérico de "valor"
                  que mostraba esta card antes. La última línea real siempre describe qué
                  podría romper la combinación (construcción determinista del backend, ver
                  `app/sgp_engine/explanation.py`), se separa en su propia sección "Qué puede
                  romperlo" en vez de mezclarla con la tesis. */}
              {explanationLines.length > 0 ? (
                <>
                  <div className="space-y-2">
                    <p className="text-[11px] font-black uppercase tracking-wider text-text-dim">Por qué este SGP</p>
                    <div className="bg-panel-elevated/40 border border-border/40 p-4 rounded-2xl space-y-2">
                      {thesisLines.map((line, i) => (
                        <div key={i} className="flex items-start gap-2.5">
                          <Layers className="h-3.5 w-3.5 text-violet shrink-0 mt-0.5" />
                          <p className="text-xs text-text-muted leading-relaxed">{line}</p>
                        </div>
                      ))}
                      <div className="flex items-center gap-2.5 pt-1">
                        <Gauge className="h-4 w-4 text-violet shrink-0" />
                        <p className="text-xs text-text-muted">
                          Calidad del análisis:{' '}
                          <strong className="text-violet">{qualityLabel(parlay.portfolio_quality_score)}</strong>
                          {parlay.portfolio_quality_score != null && ` (${parlay.portfolio_quality_score.toFixed(0)}/100)`}
                        </p>
                      </div>
                    </div>
                  </div>
                  {breaksItLine && (
                    <div className="space-y-2">
                      <p className="text-[11px] font-black uppercase tracking-wider text-text-dim">Qué puede romperlo</p>
                      <div className="bg-panel-elevated/40 border border-border/40 p-4 rounded-2xl flex items-start gap-2.5">
                        <ShieldAlert className="h-4 w-4 text-warn shrink-0 mt-0.5" />
                        <p className="text-xs text-text-muted leading-relaxed">{breaksItLine}</p>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="space-y-2">
                  <p className="text-[11px] font-black uppercase tracking-wider text-text-dim">Por qué este SGP</p>
                  <div className="bg-panel-elevated/40 border border-border/40 p-4 rounded-2xl space-y-2.5">
                    <div className="flex items-center gap-2.5">
                      <Gauge className="h-4 w-4 text-violet shrink-0" />
                      <p className="text-xs text-text-muted">
                        Calidad del análisis:{' '}
                        <strong className="text-violet">{qualityLabel(parlay.portfolio_quality_score)}</strong>
                        {parlay.portfolio_quality_score != null && ` (${parlay.portfolio_quality_score.toFixed(0)}/100)`}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Sección 10: Construcción */}
              <div className="space-y-2">
                <p className="text-[11px] font-black uppercase tracking-wider text-text-dim">Construcción</p>
                <div className="space-y-2.5">
                  {sortedLegs.map((leg) => {
                    // Sección D (25/09/2026, "State Consistency"): MISMA lectura canónica que
                    // ParlayCard.tsx/TopPickCard.tsx -- nunca `leg.settled_status` crudo.
                    const legBadgeStatus = resolvePickBadgeStatus(
                      leg.fixture_status as string | null | undefined,
                      leg.settled_status
                    )
                    return (
                    <div key={leg.leg_order} className="bg-panel-elevated/40 border border-border/40 p-3 rounded-xl space-y-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-xs font-bold text-text truncate">{(leg.selection_label ?? leg.selection) as string}</p>
                          <p className="text-[10px] text-text-dim">{(leg.market_label ?? leg.market) as string}</p>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {legBadgeStatus && <StatusBadge status={legBadgeStatus} />}
                          <span className="text-xs font-mono font-black text-text bg-panel px-2 py-1 rounded-lg border border-border">
                            @{formatOdds(leg.odds as number)}
                          </span>
                        </div>
                      </div>
                      {/* P0.32 (26/08/2026, rediseño Mini App -- Sección 3 del encargo):
                          resultado real de ESTA pierna cuando ya liquidó. */}
                      {leg.settled_status && (leg.market_result_display || leg.stat_result_display) && (
                        <div className="flex flex-wrap items-center gap-1.5">
                          {leg.market_result_display && (
                            <span className="text-[9px] font-black uppercase text-cyan bg-cyan/10 border border-cyan/20 px-2 py-0.5 rounded-lg inline-block">
                              {leg.market_result_display}
                            </span>
                          )}
                          {leg.stat_result_display && (
                            <span className="text-[9px] font-black uppercase text-cyan bg-cyan/10 border border-cyan/20 px-2 py-0.5 rounded-lg inline-block">
                              {leg.stat_result_display}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    )
                  })}
                </div>
              </div>

              {/* Sección 10: Correlación -- explicación humana, nunca matemática cruda */}
              <div className="space-y-2">
                <p className="text-[11px] font-black uppercase tracking-wider text-text-dim">Correlación</p>
                <p className="text-xs text-text-muted leading-relaxed bg-panel-elevated/40 border border-border/40 p-3.5 rounded-2xl">
                  John Anthony evalúa cuánto dependen entre sí los mercados de este partido. Se descartaron
                  combinaciones demasiado correlacionadas para evitar inflar artificialmente la cuota total
                  {parlay.correlation_level && (
                    <> -- nivel de correlación de esta combinación: <strong className="text-text">{CONFIDENCE_LABEL[parlay.correlation_level] ?? parlay.correlation_level}</strong>.</>
                  )}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2.5">
                <div className="bg-panel-elevated/50 p-3 rounded-xl border border-border/40 text-center">
                  <p className="text-[9px] font-bold uppercase tracking-wider text-text-dim">Cuota Total</p>
                  <p className="text-xl font-black text-violet font-mono">@{formatOdds(parlay.combined_odds)}</p>
                </div>
                <div className="bg-panel-elevated/50 p-3 rounded-xl border border-border/40 text-center">
                  <p className="text-[9px] font-bold uppercase tracking-wider text-text-dim">Prob. Combinada</p>
                  <p className="text-xl font-black text-text font-mono">{formatProb(parlay.joint_probability_adjusted)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPlayModal && (
        <RegisterBetModal
          onClose={() => setShowPlayModal(false)}
          matchLabel={`Same Game Parlay · ${homeName} vs ${awayName}`}
          selectionLabel={sortedLegs.map((leg) => (leg.selection_label ?? leg.selection ?? '') as string).join(' + ')}
          referenceOdds={parlay.combined_odds ?? 0}
          parlayId={parlay.id}
        />
      )}
    </>
  )
}
